"use client";

import { useEffect, useState } from "react";
import { interpretQueueHttp } from "@/lib/financialWork/queueClient";
import { mergeBatchRosterIntoCard } from "@/lib/financialWork/feedClient";
import type {
  LiffFinancialWorkCard,
  WorkQueueProjection,
} from "@/lib/financialWork/types";
import {
  cardBatchClosed,
  cardBatchSize,
  isBacklogChildPaid,
  isFromBatchCard,
} from "@/lib/financialWork/nav";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import { subscribeAuthSession } from "@/lib/authSession";

const marketApiFetch = createMarketApiFetch();

function batchRosterNeedsFetch(card: LiffFinancialWorkCard | null): boolean {
  if (!card || !isFromBatchCard(card)) return false;
  const roster = card.backlogChildren ?? [];
  const paid = roster.filter((row) => isBacklogChildPaid(row)).length;
  if (cardBatchClosed(card) > paid) return true;
  return cardBatchSize(card) > roster.length;
}

/** Fill missing paid batch mates on feed cards from the work-queue projection. */
export function useBatchRosterEnrichment(
  card: LiffFinancialWorkCard | null,
  queue: WorkQueueProjection | null | undefined,
  workspaceId: string | null,
  ownerUserId?: string | null,
): LiffFinancialWorkCard | null {
  const [fetchedQueue, setFetchedQueue] = useState<WorkQueueProjection | null>(
    null,
  );
  const [viewerId, setViewerId] = useState(() => getUserIdFromAuthToken());

  useEffect(() => subscribeAuthSession(() => setViewerId(getUserIdFromAuthToken())), []);

  useEffect(() => {
    setFetchedQueue(null);
    if (!card || !workspaceId || !viewerId) return;
    const merged = queue ? mergeBatchRosterIntoCard(card, queue) : card;
    if (!batchRosterNeedsFetch(merged)) return;

    let cancelled = false;
    const qs = new URLSearchParams({
      workspace_id: workspaceId,
      actor_id: viewerId,
    });
    const owner = ownerUserId?.trim() ?? "";
    if (owner) qs.set("owner_user_id", owner);

    void marketApiFetch(`/api/v1/financial-work/queue?${qs.toString()}`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        const outcome = interpretQueueHttp(res.status, body);
        if (cancelled || outcome.kind !== "ok") return;
        setFetchedQueue(outcome.projection);
      })
      .catch(() => {
        /* keep the visible card as-is */
      });

    return () => {
      cancelled = true;
    };
  }, [card, ownerUserId, queue, viewerId, workspaceId]);

  if (!card) return null;

  let merged = queue ? mergeBatchRosterIntoCard(card, queue) : card;
  if (fetchedQueue) {
    merged = mergeBatchRosterIntoCard(merged, fetchedQueue);
  }
  return merged;
}
